import { Permission } from '@chiroup/core/types/AdminUserRole.type';
import React from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import LoadingPage from './LoadingPage';

type Props = {
  /** One permission, or a list — caller has all listed permissions. */
  permission?: Permission;
  permissions?: Permission[];
  /** If supplied, caller needs *any one* of these. */
  anyOf?: Permission[];
  /** Element rendered when the caller has access. */
  children: React.ReactNode;
  /** Fallback element. Defaults to a permission-denied message. */
  fallback?: React.ReactNode;
};

const DefaultDenied: React.FC = () => (
  <div className="p-6 text-sm text-gray-500 dark:text-dark-gray-400">
    You do not have permission to view this page.
  </div>
);

/**
 * Route-level permission gate. Use to wrap a <Route element={...} /> so
 * that admins without the right permission see a friendly denial instead
 * of the page contents. Backend endpoints still enforce permissions; this
 * is purely for UX so users don't load pages whose calls all 403.
 */
const RequirePermission: React.FC<Props> = ({
  permission,
  permissions,
  anyOf,
  children,
  fallback,
}) => {
  const { hasPermission, hasAll, hasAny, isLoading } = useAdminPermissions();

  if (isLoading) return <LoadingPage />;

  const required: Permission[] = permission ? [permission] : (permissions ?? []);
  const ok = (required.length === 0 || hasAll(required)) && (!anyOf || hasAny(anyOf));

  if (!ok) return <>{fallback ?? <DefaultDenied />}</>;
  return <>{children}</>;
};

export default RequirePermission;
